// frontend/src/theme.js — the app's themes
// THEME_FAMILIES names every theme the app has. createAppTheme builds the MUI
// theme for the theme on <html data-theme>. Its colours come from the CSS
// tokens in styles/variables.css, read off the page: MUI does colour maths
// (hover shades, contrast text) and cannot take var(--…). main.jsx calls it
// again whenever data-theme changes.

import { createTheme } from '@mui/material/styles';

/**
 * Every theme, as families of a light and a dark theme. The values are the
 * data-theme names that styles/variables.css defines tokens for.
 */
export const THEME_FAMILIES = [
  { key: 'classic', light: 'light', dark: 'dark' },
  { key: 'navy', light: 'sky', dark: 'navy' },
  { key: 'forest', light: 'meadow', dark: 'forest' },
];

/**
 * True when a data-theme value is the dark half of its family. A page with no
 * data-theme yet shows the :root tokens, which are the light ones.
 */
export const isDarkTheme = (name) => THEME_FAMILIES.some((family) => family.dark === name);

/** The family a data-theme value belongs to. */
export function themeFamily(name) {
  const family = THEME_FAMILIES.find((f) => f.light === name || f.dark === name);
  if (!family) throw new Error(`Unknown theme "${name}"`);
  return family;
}

/**
 * How long the pointer rests on an element before a first hint shows — the
 * MUI Tooltip and the AG Grid tooltip alike (utils/gridTooltip.js). Moving
 * on from an open MUI hint opens the next one at once.
 */
export const TOOLTIP_SHOW_DELAY_MS = 300;

export function createAppTheme() {
  const root = document.documentElement;
  const styles = getComputedStyle(root);
  const token = (name) => {
    const value = styles.getPropertyValue(`--color-${name}`).trim();
    if (!value) throw new Error(`createAppTheme: CSS token --color-${name} is not set on this page`);
    return value;
  };

  return createTheme({
    typography: {
      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
      // The app has two weights, regular and semibold (styles/base.css
      // declares the faces; fontMetrics.test.js keeps every weight the app
      // names on one of them). MUI's own light, medium and bold are folded
      // onto them: buttons and titles regular, bold text semibold.
      fontWeightLight: 400,
      fontWeightMedium: 400,
      fontWeightBold: 600,
      button: {
        textTransform: 'none',
      },
    },
    components: {
      MuiChip: {
        defaultProps: {
          size: 'small',
          variant: 'outlined',
        },
        styleOverrides: {
          // MUI outlines a default-colour chip in its own grey palette.
          root: { '&.MuiChip-outlined.MuiChip-colorDefault': { borderColor: 'var(--color-border)' } },
        },
      },
      MuiButton: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiTooltip: {
        // Every hint sits above its element and points at it. tooltips.test.js
        // keeps call sites from setting their own shape.
        defaultProps: { arrow: true, placement: 'top', enterDelay: TOOLTIP_SHOW_DELAY_MS },
        styleOverrides: {
          // The app's one tooltip card (styles/variables.css, TOOLTIPS) instead
          // of MUI's grey, and a line break in the copy shows. The arrow is the
          // card's colour with the card's border on its two outer sides.
          tooltip: {
            fontSize: 'var(--font-size-tooltip)',
            fontWeight: 'var(--font-weight-normal)',
            whiteSpace: 'pre-line',
            backgroundColor: 'var(--tooltip-background)',
            color: 'var(--tooltip-text)',
            border: 'var(--tooltip-border)',
            borderRadius: 'var(--tooltip-radius)',
            boxShadow: 'var(--tooltip-shadow)',
            padding: 'var(--tooltip-padding)',
            maxWidth: 'var(--tooltip-max-width)',
          },
          arrow: {
            color: 'var(--tooltip-background)',
            '&::before': { border: 'var(--tooltip-border)' },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          // In dark mode MUI lays a white gradient over elevated paper, which
          // would lift dialogs and menus off the --color-surface token.
          root: { backgroundImage: 'none' },
        },
      },
      MuiDialog: {
        defaultProps: {
          // The paper spans its box and no MUI breakpoint caps it: the width
          // is the popup's size class (styles/dialog-form.css, "Width").
          maxWidth: false,
          fullWidth: true,
        },
      },
      MuiDialogActions: {
        // A button keeps its label on one line (styles/mui-overrides.css), so
        // a bar too narrow for its buttons wraps them. MUI spaces them with a
        // left margin, which would indent the first button of a wrapped row.
        defaultProps: { disableSpacing: true },
        styleOverrides: { root: { flexWrap: 'wrap', gap: 'var(--spacing-sm)' } },
      },
      MuiAlert: {
        defaultProps: {
          // Colour carries the severity; an icon would make every banner and
          // toast taller and push its text right.
          icon: false,
        },
      },
    },
    palette: {
      mode: isDarkTheme(root.getAttribute('data-theme')) ? 'dark' : 'light',
      primary: { main: token('primary') },
      error: { main: token('error') },
      warning: { main: token('warning') },
      success: { main: token('success') },
      info: { main: token('info') },
      text: { primary: token('text-primary'), secondary: token('text-secondary') },
      background: { default: token('fill'), paper: token('surface') },
      divider: token('border'),
    },
  });
}
